import React, { useState, useEffect } from 'react';
import { getBookmarks, saveBookmark, deleteBookmark } from '../../services/powerbiService';
import { useTranslation } from '../../i18n';
import useActivityTracker from '../../hooks/useActivityTracker';
import './BookmarkManager.css';

const BookmarkManager = ({
  report,
  username,
  reportId,
  isVisible,
  onClose
}) => {
  const { t } = useTranslation();
  const { trackAction } = useActivityTracker(username, 'reports');
  const [bookmarks, setBookmarks] = useState([]);
  const [bookmarkName, setBookmarkName] = useState('');
  const [bookmarkDescription, setBookmarkDescription] = useState('');
  const [isCapturing, setIsCapturing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [notification, setNotification] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  useEffect(() => {
    if (isVisible && username && reportId) {
      loadBookmarks();
    }
  }, [isVisible, username, reportId]);

  // Auto-hide notification after 3s
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => setNotification(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
  };

  const loadBookmarks = async () => {
    setIsLoading(true);
    try {
      const data = await getBookmarks(username, reportId);
      setBookmarks(data || []);
    } catch (error) {
      console.error('[BookmarkManager] Error loading bookmarks:', error);
      setBookmarks([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCaptureBookmark = async () => {
    if (!report) {
      showNotification(t('reports.filterError'), 'error');
      return;
    }

    if (!bookmarkName.trim()) {
      showNotification(t('reports.enterFilterName'), 'warning');
      return;
    }

    setIsCapturing(true);
    try {
      if (!report.bookmarksManager) {
        throw new Error('BookmarksManager not available');
      }

      const capturedBookmark = await report.bookmarksManager.capture({
        personalizeVisuals: true,
        allPages: false
      });

      const bookmarkState = capturedBookmark.state;

      await saveBookmark(
        username,
        reportId,
        bookmarkName.trim(),
        bookmarkState,
        bookmarkDescription.trim()
      );

      trackAction('save_bookmark', {
        report_id: reportId,
        bookmark_name: bookmarkName.trim(),
        has_description: !!bookmarkDescription.trim()
      });

      showNotification(t('reports.filterSaved'), 'success');
      setBookmarkName('');
      setBookmarkDescription('');
      await loadBookmarks();
    } catch (error) {
      console.error('[BookmarkManager] Error capturing bookmark:', error);
      showNotification(`${t('reports.filterError')}: ${error.message}`, 'error');
    } finally {
      setIsCapturing(false);
    }
  };

  const handleApplyBookmark = async (bookmark) => {
    if (!report) {
      showNotification(t('reports.filterError'), 'error');
      return;
    }

    try {
      if (!report.bookmarksManager) {
        throw new Error('BookmarksManager not available');
      }

      await report.bookmarksManager.applyState(bookmark.bookmark_state);
      trackAction('apply_bookmark', {
        report_id: reportId,
        bookmark_id: bookmark.id,
        bookmark_name: bookmark.name
      });
      showNotification(t('reports.filterApplied'), 'success');
      onClose();
    } catch (error) {
      console.error('[BookmarkManager] Error applying bookmark:', error);
      showNotification(`Error: ${error.message}`, 'error');
    }
  };

  const handleDeleteBookmark = async (bookmarkId, bookmarkName) => {
    try {
      await deleteBookmark(bookmarkId, username);
      trackAction('delete_bookmark', {
        report_id: reportId,
        bookmark_id: bookmarkId,
        bookmark_name: bookmarkName
      });
      showNotification(t('reports.filterDeleted'), 'success');
      setConfirmDeleteId(null);
      await loadBookmarks();
    } catch (error) {
      console.error('[BookmarkManager] Error deleting bookmark:', error);
      showNotification(`Error: ${error.message}`, 'error');
    }
  };

  if (!isVisible) return null;

  return (
    <div className="bm-overlay" onClick={onClose}>
      <div className="bm-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="bm-header">
          <h2>{t('reports.myFilters')}</h2>
          <button className="bm-close-btn" onClick={onClose} aria-label="Close">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Notification Banner */}
        {notification && (
          <div className={`bm-notification bm-notification-${notification.type}`}>
            {notification.message}
          </div>
        )}

        <div className="bm-body">
          {/* Capture Section */}
          <div className="bm-capture-section">
            <h3>{t('reports.saveCurrentFilters')}</h3>
            <input
              type="text"
              placeholder={t('reports.filterNamePlaceholder')}
              value={bookmarkName}
              onChange={(e) => setBookmarkName(e.target.value)}
              maxLength={100}
              className="bm-input"
            />
            <textarea
              placeholder={t('reports.filterDescription')}
              value={bookmarkDescription}
              onChange={(e) => setBookmarkDescription(e.target.value)}
              maxLength={250}
              rows={2}
              className="bm-textarea"
            />
            <button
              className="bm-btn-capture"
              onClick={handleCaptureBookmark}
              disabled={isCapturing || !bookmarkName.trim()}
            >
              {isCapturing ? (
                <>
                  <span className="bm-btn-spinner"></span>
                  {t('reports.saving')}
                </>
              ) : (
                <>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                    <polyline points="17 21 17 13 7 13 7 21" />
                    <polyline points="7 3 7 8 15 8" />
                  </svg>
                  {t('reports.saveCurrentFilters')}
                </>
              )}
            </button>
          </div>

          <div className="bm-divider"></div>

          {/* Bookmarks List */}
          <div className="bm-list-section">
            <h3>{t('reports.savedFilters')} ({bookmarks.length})</h3>

            {isLoading ? (
              <div className="bm-loading">
                <div className="bm-spinner"></div>
                <p>{t('common.loading')}</p>
              </div>
            ) : bookmarks.length === 0 ? (
              <div className="bm-empty">
                <p>{t('reports.noFilters')}</p>
                <p className="bm-empty-hint">{t('reports.noFiltersHint')}</p>
              </div>
            ) : (
              <div className="bm-list">
                {bookmarks.map((bookmark) => (
                  <div key={bookmark.id} className="bm-item">
                    <div className="bm-item-info">
                      <div className="bm-item-name">
                        {bookmark.bookmark_name}
                        {bookmark.is_default && (
                          <span className="bm-default-badge">{t('reports.default')}</span>
                        )}
                      </div>
                      {bookmark.description && (
                        <div className="bm-item-description">{bookmark.description}</div>
                      )}
                      <div className="bm-item-meta">
                        {t('reports.savedOn')}: {new Date(bookmark.created_at).toLocaleDateString(undefined, {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                    </div>
                    <div className="bm-item-actions">
                      {confirmDeleteId === bookmark.id ? (
                        <div className="bm-confirm-delete">
                          <button
                            className="bm-btn-confirm-yes"
                            onClick={() => handleDeleteBookmark(bookmark.id, bookmark.bookmark_name)}
                          >
                            {t('common.confirm')}
                          </button>
                          <button
                            className="bm-btn-confirm-no"
                            onClick={() => setConfirmDeleteId(null)}
                          >
                            {t('common.cancel')}
                          </button>
                        </div>
                      ) : (
                        <>
                          <button
                            className="bm-btn-apply"
                            onClick={() => handleApplyBookmark(bookmark)}
                            title={t('reports.apply')}
                          >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="14" height="14">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                            {t('reports.apply')}
                          </button>
                          <button
                            className="bm-btn-delete"
                            onClick={() => setConfirmDeleteId(bookmark.id)}
                            title={t('reports.deleteFilter')}
                          >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
                              <polyline points="3 6 5 6 21 6" />
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                            </svg>
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookmarkManager;
